import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { getDrilldown } from '../core/api'
import { nf } from '../core/consts'
import { formatMskDateTime } from '../core/mskTime'

type Props = {
  signal: string | null
  title?: string
  currency?: string
  merchant?: number
  onClose: () => void
}

// Human-readable column headers (managers must not see raw ids).
const COLUMN_LABELS: Record<string, string> = {
  created_at: 'Создан',
  token: 'Токен',
  id: 'ID',
  merchant: 'Мерчант',
  method: 'Метод',
  amount: 'Сумма',
  currency: 'Валюта',
  amount_group: 'Корзина',
  age_min: 'Возраст',
  active_count: 'Активных',
  group_limit: 'Лимит',
  available_payouts_count: 'Доступно выплат',
  reason: 'Причина',
  color: 'Цвет',
  matching_transaction_id: 'ID матч-транзакции',
  entered: 'Вошло в мэтчинг',
  got_requisite: 'Получили реквизит',
  rate_pct: 'Выдача, %',
}

// Which matching page a signal's rows belong to (for row → ticket deep-link).
const SIGNAL_TARGET: Record<string, string> = {
  payouts_overdue: '/matching/payout/active',
  active_payouts: '/matching/payout/active',
  basket_full: '/matching/payout/active',
  flowcontrol_rejects: '/matching/payout/active',
  active_deposits: '/matching/payment/active',
  deposits_expiring: '/matching/payment/active',
  demand_shortfall: '/matching/payment/active',
  antifraud_red: '/matching/payment/active',
  declined_payouts: '/matching/payout/active',
  stuck_payouts: '/matching/payout/active',
}

const COLOR_LABELS: Record<string, string> = {
  red: 'Красный',
  yellow: 'Жёлтый',
  green: 'Зелёный',
  black: 'Чёрный (сбой OCR)',
}

// Preferred left-to-right column order; unknown keys are appended.
const ORDER = [
  'created_at', 'token', 'merchant', 'entered', 'got_requisite', 'rate_pct',
  'method', 'amount', 'currency', 'amount_group',
  'age_min', 'active_count', 'group_limit', 'available_payouts_count', 'reason', 'color',
]

function orderedColumns(rows: Array<Record<string, unknown>>): string[] {
  const keys = new Set<string>()
  rows.forEach((r) => Object.keys(r).forEach((k) => keys.add(k)))
  const known = ORDER.filter((k) => keys.has(k))
  const rest = Array.from(keys).filter((k) => !ORDER.includes(k))
  return [...known, ...rest]
}

// Разбор и перевод в МСК — общий для всего дашборда (core/mskTime.ts), чтобы
// правило «нет пояса ⇒ это UTC» жило в одном месте.
const toMsk = formatMskDateTime

function fmt(col: string, v: unknown): string {
  if (v === null || v === undefined || v === '') return '—'
  if (col === 'created_at') {
    return toMsk(v)
  }
  if (col === 'amount') {
    const n = Number(v)
    return Number.isFinite(n) ? nf.format(n) : String(v)
  }
  if (col === 'age_min') {
    const m = Number(v)
    if (!Number.isFinite(m)) return String(v)
    return m >= 60 ? `${Math.floor(m / 60)}ч ${m % 60}м` : `${m}м`
  }
  if (col === 'color') {
    return COLOR_LABELS[String(v)] ?? String(v)
  }
  return String(v)
}

const DrilldownModal: React.FC<Props> = ({ signal, title, currency, merchant, onClose }) => {
  const [rows, setRows] = useState<Array<Record<string, unknown>>>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!signal) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setRows([])
    getDrilldown(signal, { currency, merchant })
      .then((resp) => {
        if (cancelled) return
        if (resp.data.success) {
          setRows(resp.data.data.items ?? [])
        } else {
          setError('Не удалось загрузить детализацию')
        }
      })
      .catch(() => !cancelled && setError('Сервис мэтчинга недоступен'))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [signal, currency, merchant])

  const columns = orderedColumns(rows)
  const target = signal ? SIGNAL_TARGET[signal] : undefined

  const openTicket = (token: unknown) => {
    if (!target || !token) return
    window.open(`${target}?ticket=${encodeURIComponent(String(token))}`, '_blank', 'noopener')
  }

  return (
    <Modal show={signal !== null} onHide={onClose} centered dialogClassName='matching-drilldown-dialog'>
      {/* Fit width to the table up to 90vw; body scrolls within 70vh. */}
      <style>{`.matching-drilldown-dialog{max-width:90vw;width:-moz-fit-content;width:fit-content;min-width:420px;}`}</style>
      <Modal.Header closeButton>
        <Modal.Title>{title ?? 'Детализация'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <div className='text-muted'>Загрузка…</div>}
        {error && <div className='text-danger'>{error}</div>}
        {!loading && !error && rows.length === 0 && <div className='text-muted'>Нет данных</div>}
        {!loading && !error && rows.length > 0 && (
          <div className='table-responsive' style={{ maxHeight: '70vh', overflow: 'auto' }}>
            <table className='table table-sm table-striped align-middle text-nowrap mb-0'>
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c} style={c === 'token' ? { minWidth: 320 } : undefined}>
                      {COLUMN_LABELS[c] ?? c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const clickable = !!target && !!row.token
                  return (
                    <tr
                      key={i}
                      className={clickable ? 'cursor-pointer' : undefined}
                      title={clickable ? 'Открыть тикет в мэтчинге (новая вкладка)' : undefined}
                      onClick={clickable ? () => openTicket(row.token) : undefined}
                    >
                      {columns.map((c) => (
                        <td key={c} style={c === 'token' ? { minWidth: 320, fontFamily: 'monospace' } : undefined}>
                          {fmt(c, row[c])}
                        </td>
                      ))}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default DrilldownModal
